import React, { useMemo } from 'react';
import { ResponsiveContainer, LineChart, Line, YAxis, Tooltip, ReferenceLine } from 'recharts';

export type CashflowMiniRow = {
    /** YYYY-MM */
    month: string;
    /** 누적 순현금 (currency 단위) */
    netCum: number;
};

type Props = {
    title?: string;
    data: CashflowMiniRow[];
    currency?: string | null;
    height?: number;
    onOpenDetail?: () => void;

    /** ProgressOverview에서 접기/펴기 제어 */
    collapsed: boolean;
    onToggleCollapsed: () => void;
};

function fmtCompact(n: number) {
    const v = Number.isFinite(n) ? n : 0;
    return new Intl.NumberFormat('en-US', { notation: 'compact', maximumFractionDigits: 1 }).format(v);
}

function normalizeMonthLabel(m: string) {
    // 'YYYY-MM' or 'YYYY-MM-DD' -> 'YYYY-MM'
    if (!m) return '—';
    return m.length >= 7 ? m.slice(0, 7) : m;
}

// function normalizeMonthLabel(m: string) {
//     if (!m || m.length < 7) return '-';

//     const y = m.slice(2, 4);    // '2026' -> '26'
//     const mm = Number(m.slice(5, 7)); // '07' -> 7

//     return `${y}년 ${mm}월`;
// }

function formatMonthKo(m?: string) {
    if (!m || m.length < 7) return '—';

    const yy = m.slice(2, 4); // 2026 → 26
    const mm = Number(m.slice(5, 7)); // 07 → 7
    return `${yy}년 ${mm}월`;
}

function CustomTooltip({ active, payload, currency }: { active?: boolean; payload?: any[]; currency?: string | null }) {
    if (!active || !payload?.length) return null;

    // 실제 데이터 row
    const row = payload[0].payload as { month?: string; netCum?: number };

    // console.log('row.month : ', row.month); // '2026-07'
    const v = Number(row?.netCum ?? 0);
    const ym = formatMonthKo(row.month);

    return (
        <div className="rounded-xl border bg-white/95 shadow-sm px-3 py-2">
            <div className="text-[11px] text-gray-500 mb-1">{ym}</div>
            <div className="flex items-center justify-between gap-6 text-xs">
                <span className="text-gray-600">누적 순현금</span>
                <span className="font-semibold tabular-nums">
                    {fmtCompact(Math.abs(v))} {currency ?? ''}
                    {v < 0 ? ' (−)' : ''}
                </span>
            </div>
        </div>
    );
}

export default function CashflowMini({
    title = '누적 순현금',
    data,
    currency,
    height = 140,
    collapsed,
    onToggleCollapsed,
    onOpenDetail,
}: Props) {
    const meta = useMemo(() => {
        if (!data?.length) {
            return {
                startMonth: '—',
                endMonth: '—',
                startVal: 0,
                endVal: 0,
            };
        }
        const first = data[0];
        const last = data[data.length - 1];
        return {
            startMonth: normalizeMonthLabel(first.month),
            endMonth: normalizeMonthLabel(last.month),
            startVal: Number(first.netCum ?? 0) || 0,
            endVal: Number(last.netCum ?? 0) || 0,
        };
    }, [data]);

    const endTone = meta.endVal < 0 ? 'text-rose-700' : 'text-sky-700';

    return (
        <div className="relative h-full w-full min-w-0 rounded-2xl border bg-white p-4 shadow-sm">
            {/* Header */}
            <div className="flex items-start justify-between gap-2">
                <div className="min-w-0">
                    <div className="text-sm font-semibold text-gray-900">{title}</div>
                    {/* {!collapsed && (
                        <div className="mt-0.5 text-xs text-gray-500">
                            시작 {meta.startMonth} → 현재 {meta.endMonth}
                        </div>
                    )} */}
                </div>

                {!collapsed ? (
                    <button
                        type="button"
                        onClick={onToggleCollapsed}
                        className="shrink-0 rounded-lg border px-2 py-1 text-xs text-gray-700 hover:bg-gray-50"
                        title="접기"
                    >
                        접기
                    </button>
                ) : (
                    <button
                        type="button"
                        onClick={onToggleCollapsed}
                        className="shrink-0 rounded-lg border px-2 py-1 text-xs text-gray-700 hover:bg-gray-50"
                        title="펴기"
                    >
                        펴기
                    </button>
                )}
            </div>
            {collapsed && <div className="mt-2 text-[11px] text-gray-500">(펼치면 월별 누적 커브가 표시됩니다)</div>}

            {/* Collapsed */}
            {!collapsed && (
                <>
                    {/* ✅ start/end 의미 라벨 (차트 위에 간단히) */}
                    <div className="mt-2 flex items-center justify-between text-[11px] text-gray-500">
                        <div className="min-w-0">
                            <span className="font-medium text-gray-700">Start</span>
                            <span className="ml-1">{meta.startMonth}</span>
                            {/* <span className="ml-2 tabular-nums">
                                {fmtCompact(Math.abs(meta.startVal))} {currency ?? ''}
                                {meta.startVal < 0 ? ' (−)' : ''}
                            </span> */}
                        </div>

                        <div className={`min-w-0 text-right ${endTone}`}>
                            <span className="font-medium">현재</span>
                            <span className="ml-1">{meta.endMonth}</span>
                            <span className="ml-2 tabular-nums font-semibold">
                                {fmtCompact(Math.abs(meta.endVal))} {currency ?? ''}
                                {meta.endVal < 0 ? ' (−)' : ''}
                            </span>
                        </div>
                    </div>

                    {/* Chart */}
                    <div className="mt-2" style={{ height }}>
                        {!data?.length ? (
                            <div className="h-full rounded-xl border bg-gray-50 flex items-center justify-center text-sm text-gray-500">
                                데이터 없음
                            </div>
                        ) : (
                            <ResponsiveContainer width="100%" height="100%">
                                <LineChart data={data}>
                                    {/* x축 label은 숨기고 tooltip로만 보여줌 */}
                                    <YAxis hide domain={['auto', 'auto']} />
                                    <Tooltip content={<CustomTooltip currency={currency} />} />
                                    <ReferenceLine y={0} stroke="rgb(156 163 175)" strokeDasharray="4 4" />
                                    <Line
                                        type="monotone"
                                        dataKey="netCum"
                                        dot={false}
                                        stroke="rgb(15 23 42)"
                                        strokeWidth={2.5}
                                    />
                                </LineChart>
                            </ResponsiveContainer>
                        )}
                    </div>

                    <div className="mt-2 text-[11px] text-gray-500">
                        0 아래로 내려가면 “받은 돈보다 나간 돈이 더 많음”
                    </div>

                    {/* Detail */}
                    {onOpenDetail && (
                        <button
                            type="button"
                            onClick={onOpenDetail}
                            className="absolute bottom-3 right-3 inline-flex items-center gap-1 rounded-lg border bg-white px-2 py-1 text-[11px] text-gray-700 hover:bg-gray-50"
                            title="Payments 상세보기"
                        >
                            상세보기{' '}
                            <span aria-hidden className="text-[12px]">
                                ↗
                            </span>
                        </button>
                    )}
                </>
            )}
        </div>
    );
}
